"use client";

import useDates from "@/hooks/useDates";
import { Button, NumberField, RenderIf, Typography } from "@repo/ui";
import {
  Calendar,
  Clock,
  Monitor,
  Building,
  CheckCircle,
  ChevronDown,
  LanguagesIcon,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { formatCurrency } from "@/lib/helpers";
import { useCountryCode } from "@/hooks/useCountryCode";
import RegistrationCloseTimer from "../common/RegistrationCloseTimer";
import clsx from "clsx";
import { useAddCartItemMutation } from "@/store/features/cartApiSlice";
import { getCookie, hasCookie } from "cookies-next";
import FastFillingLabel from "../common/FastFillingLabel";

type ScheduleCardProps = {
  data: ServiceEvent;
};

const ScheduleCard = ({ data }: ScheduleCardProps) => {
  const [showOffers, setShowOffers] = useState(true);
  const [quantity, setQuantity] = useState<number>(1);
  const { country } = useCountryCode();
  const router = useRouter();
  const { formattedDate, weekLabel, numberOfDays } = useDates(data.eventDates);
  const days = numberOfDays > 1 ? "Days" : "Day";

  const [addToCart, { isLoading }] = useAddCartItemMutation();

  const handleAddToCart = () => {
    const guestUserId = hasCookie("guestId")
      ? String(getCookie("guestId"))
      : undefined;
    addToCart({
      countryCode: country,
      guestUserId: guestUserId,
      serviceId: data!.serviceId,
      eventId: data!.id,
      offerId: data!.offerId,
      eventDetailId: data!.eventDetail.id,
      quantity: quantity || 1,
    }).then(() => {
      router.push(`/${country}/order-summary`);
    });
  };

  return (
    <span className="relative">
      <RegistrationCloseTimer closeDate={data?.eventCloseDate} />

      <div
        className={clsx(
          "bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl border relative",
          {
            "border-2 border-secondary-main": data?.eventCloseDate,
          }
        )}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Left section with date and time */}
          <div className="bg-primary-light/10 p-8 lg:w-2/5">
            <div className="flex flex-wrap gap-2 mb-4">
              {weekLabel && (
                <span
                  className={clsx(
                    "inline-block px-3 py-1 rounded-full text-xs font-semibold transition-transform hover:scale-105 border",
                    {
                      "bg-white text-primary-dark border-primary-main/40":
                        weekLabel === "Weekday",
                      "bg-white text-error-dark border-error-main/40":
                        weekLabel === "Weekend",
                    }
                  )}
                >
                  {weekLabel}
                </span>
              )}
              {data.isEarlyBirdOfferApplied && (
                <span className="inline-block px-3 py-1 border-green-500 border bg-green-100 text-green-800 rounded-full text-xs font-medium transition-transform hover:scale-105">
                  Earlybird
                </span>
              )}
            </div>
            {data?.hasMappedService && (
              <Typography
                variant="h5"
                render={<span />}
                className="block mb-2 font-semibold"
              >
                {data?.eventName}
              </Typography>
            )}

            <div className="flex items-start space-x-3 mb-4">
              <Calendar
                className="text-primary-main mt-1 flex-shrink-0"
                size={18}
              />
              <div>
                <h3 className="font-semibold text-gray-900">{formattedDate}</h3>
                <p className="text-xs text-gray-600">
                  {numberOfDays} {days}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Clock
                className="text-primary-main mt-1 flex-shrink-0"
                size={18}
              />
              <div>
                <p className="font-medium text-gray-900">
                  {data.eventDetail.startTime} to {data.eventDetail.endTime}
                </p>
                <p className="text-xs text-gray-600">
                  {data.eventDetail.timezone}{" "}
                  <span>({data.eventDetail.timeDiffInHours} Hrs / Day)</span>
                </p>
              </div>
            </div>

            {/* Trainer information */}
            {data.eventDetail.trainer && (
              <div className="mt-3">
                <div className="flex items-center space-x-3">
                  {data.eventDetail.trainer.image && (
                    <Image
                      src={data.eventDetail.trainer.image}
                      alt={data.eventDetail.trainer.name}
                      width={96}
                      height={96}
                      aria-label={data.eventDetail.trainer.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-primary-main shadow-sm flex-shrink-0"
                    />
                  )}

                  <div>
                    <Typography
                      render={<div />}
                      className="!font-regular !text-sm text-gray-900"
                    >
                      {data.eventDetail.trainer?.name}
                    </Typography>
                    <Typography className="!text-xs text-gray-600">
                      {data.eventDetail.trainer?.experience}
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right section with training details */}
          <div className="flex-1 p-8">
            <div className="flex flex-col md:flex-row md:items-start items-stretch  md:justify-between gap-2">
              <div className="mb-4 md:mb-0">
                <div className="flex items-center space-x-2 mb-2">
                  {data.modeOfTraining === "online" ? (
                    <Monitor className="text-primary-main" size={18} />
                  ) : (
                    <Building className="text-primary-main" size={18} />
                  )}
                  <span className="font-medium text-gray-900">
                    {data.modeOfTraining}
                  </span>
                </div>

                <div className="flex items-center space-x-2 mb-2 text-green-700">
                  <CheckCircle size={18} />
                  <span className="text-sm font-medium">Guaranteed to run</span>
                </div>
                <RenderIf condition={data.isEarlyBirdOfferApplied}>
                  <div className="flex items-center space-x-2 mb-2 text-green-700">
                    <CheckCircle size={18} />
                    <span className="text-sm font-medium">
                      Special Offer Applied + 4 % Processing Fees Waiver
                    </span>
                  </div>
                </RenderIf>

                <div className="flex items-center space-x-2 mb-2 ">
                  <LanguagesIcon size={18} className="text-primary-main" />
                  <span className="text-sm font-medium">English</span>
                </div>

                <div className="text-sm text-gray-700 mb-2">
                  <b>Exam:</b>{" "}
                  <span className="font-medium">
                    {data.withExam ? "Included" : "Not Included"}
                  </span>
                </div>

                <div className="text-sm text-gray-700 mb-2">
                  <b>Certificate:</b>{" "}
                  <span className="font-medium">Course Completion</span>
                </div>

                {data.offers.length > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => setShowOffers(!showOffers)}
                      className="flex items-center text-primary-main hover:text-primary-main text-sm font-medium transition-colors"
                    >
                      <ChevronDown
                        size={16}
                        className={`mr-1 transition-transform duration-200 ${
                          showOffers ? "rotate-180" : ""
                        }`}
                      />
                      {data.offers.length} Special Offers Available!
                    </button>

                    {showOffers && (
                      <div className="mt-2 pl-5 border-l-2 border-blue-100">
                        {data.offers.map((offer, index) => (
                          <div
                            key={index}
                            className="text-sm text-gray-700 mb-2 last:mb-0"
                          >
                            {offer.description}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col md:items-end flex-shrink-0">
                <div className="flex items-center space-x-2 mb-3">
                  <NumberField
                    label="Learners:"
                    inline
                    min={1}
                    onValueChange={(value) => setQuantity(value || 1)}
                  />
                </div>

                <div className="flex flex-col md:items-end mb-3">
                  <div className="flex items-center mb-1">
                    <span className="text-2xl font-bold text-blue-900">
                      {formatCurrency(
                        data.eventDetail.price * quantity,
                        data.eventDetail.currency
                      )}
                    </span>
                    <RenderIf condition={data.eventDetail.discountValue > 0}>
                      <span className="ml-2 text-sm line-through text-gray-500">
                        {formatCurrency(
                          (data.eventDetail.price +
                            data.eventDetail.discountValue) *
                            quantity,
                          data.eventDetail.currency
                        )}
                      </span>
                    </RenderIf>
                  </div>
                  <RenderIf condition={data.eventDetail.discountValue > 0}>
                    <span className="text-sm font-medium text-green-700">
                      Save {data.eventDetail.discountPercent} (
                      {formatCurrency(
                        data.eventDetail.discountValue * quantity,
                        data.eventDetail.currency
                      )}
                      )
                    </span>
                  </RenderIf>
                </div>

                <div className="grid gap-3 grid-flow-row">
                  <Button
                    color="primary"
                    fullWidth
                    loading={isLoading}
                    onClick={handleAddToCart}
                  >
                    Enroll now
                  </Button>
                  <Button
                    color="primary"
                    variant="outlined"
                    size="small"
                    href="#request-for-group-training"
                  >
                    Request For Group Training
                  </Button>
                  <RenderIf condition={data.isFastFilling}>
                    <FastFillingLabel />
                  </RenderIf>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </span>
  );
};

export default ScheduleCard;
